import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { BILLING, EDIT, ROLE_ADMIN, ROLE_VIEWER, SETTINGS, VIEW } from "./Permissions";
import DataUtils from "./DataUtils";

const cookieDomain = process.env.REACT_APP_COOKIE_HOST;
const removeCookieOptions = { path: "/", domain: cookieDomain };

const userAccessTokenCookieName = "userAccessToken";
const userRefreshTokenCookieName = "userRefreshToken";
const providerNameCookieName = "providerName";
const redirectOnLoginCookieName = "redirectOnLogin";
const redirectOnLogoutCookieName = "redirectOnLogout";
class TokenService {
    getUserName() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            var decoded = jwtDecode(token);
            return decoded.email;
        } else {
            return "";
        }
    }

    getProviderName() {
        var providerName = Cookies.get(providerNameCookieName);
        if (!providerName) {
            var token = Cookies.get(userRefreshTokenCookieName);
            if (token) {
                var decoded = jwtDecode(token);
                var pn = decoded.provName;
                var refreshTokenExpirationTime = 1000 * 60 * 60 * 24 * 7; // 7 days
                var inSevenDays = new Date(new Date().getTime() + refreshTokenExpirationTime);
                const refreshTokenCookieOptions = {
                    path: "/",
                    secure: true,
                    expires: inSevenDays,
                    domain: cookieDomain,
                };
                Cookies.set(providerNameCookieName, pn, refreshTokenCookieOptions);
                return pn;
            } else {
                return "";
            }
        }
        if (providerName === "undefined") {
            return "";// when impersonating accounts, I don't think we set this correctly when refreshing
        }
        return providerName;
    }

    getProviderVanityUrl() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            var decoded = jwtDecode(token);
            if (!decoded.provVan || decoded.provVan === "") {
                return null;
            }
            return decoded.provVan;
        } else {
            return null;
        }
    }

    getProviderSubdomain() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            var decoded = jwtDecode(token);
            if (!decoded.provSub || decoded.provSub === "") {
                return null;
            }
            return decoded.provSub;
        } else {
            return null;
        }
    }

    getProviderUrl() {
        return process.env.REACT_APP_PROVIDER_SITE + "/provider/" + this.getProviderSubdomain();
    }

    setAccessToken(token) {
        const accessTokenExpirationTime = 15 * 60 * 1000; // 15 minutes
        var inFifteenMinutes = new Date(new Date().getTime() + accessTokenExpirationTime);
        const accessTokenCookieOptions = {
            path: "/",
            secure: true,
            expires: inFifteenMinutes,
            domain: cookieDomain,
        };
        Cookies.set(userAccessTokenCookieName, token, accessTokenCookieOptions);
    }

    setRefreshToken(token) {
        var refreshTokenExpirationTime = 1000 * 60 * 60 * 24 * 7; // 7 days
        var inSevenDays = new Date(new Date().getTime() + refreshTokenExpirationTime);
        const refreshTokenCookieOptions = {
            path: "/",
            secure: true,
            expires: inSevenDays,
            domain: cookieDomain,
        };
        var decoded = jwtDecode(token);
        var pn = decoded.provName;
        Cookies.set(providerNameCookieName, pn, refreshTokenCookieOptions);
        Cookies.set(userRefreshTokenCookieName, token, refreshTokenCookieOptions);
    }

    isAuthenticated() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            return true;
        }
        return false;
    }

    isLoggedIn() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            var decoded = jwtDecode(token);
            var emailVerified = decoded.ev;
            if (emailVerified === "true") {
                return true;
            }
            else {
                return false;
            }
        }
        return false;
    }

    isWallistAdmin() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            var decoded = jwtDecode(token);
            var emailVerified = decoded.ev;
            if (emailVerified === "true") {
                var email = decoded.email.trim();
                if (email.endsWith("@wallist.com")) {
                    var sa = decoded.sa;
                    if (sa === "true") {
                        return true;
                    }
                    else {
                        return false;
                    }
                }
                return false;
            }
            else {
                return false;
            }
        }
        return false;
    }

    isPendingEmailVerification() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            var decoded = jwtDecode(token);
            var emailVerified = decoded.ev;
            if (emailVerified === "false") {
                return true;
            }
            else {
                return false;
            }
        }
        return false;
    }

    isUserProfileComplete() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            try {
                var decoded = jwtDecode(token);
                var userProfileComplete = decoded.upc;
                console.log("userProfileComplete:", userProfileComplete);
                // Handle string values (with potential whitespace)
                if (typeof userProfileComplete === "string") {
                    var normalized = userProfileComplete.trim().toLowerCase();
                    console.log("normalized:", normalized);
                    return normalized === "true";
                }
                
                // Handle boolean values
                if (typeof userProfileComplete === "boolean") {
                    console.log("userProfileComplete is boolean:", userProfileComplete);
                    return userProfileComplete === true;
                }
                
                // For any other value (null, undefined, etc.), return false
                return false;
            } catch (error) {
                // If token decoding fails, assume profile is not complete
                console.error("Error decoding token in isUserProfileComplete:", error);
                return false;
            }
        }
        return false;
    }

    isLoggedIntoProvider() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            var decoded = jwtDecode(token);
            if (decoded.provSub) {
                return true;
            }
        }
        return false;
    }

    logoutUser() {
        Cookies.remove(userAccessTokenCookieName, removeCookieOptions);
        Cookies.remove(userRefreshTokenCookieName, removeCookieOptions);
        Cookies.remove(providerNameCookieName, removeCookieOptions);
    }

    getUserAccessToken() {
        return Cookies.get(userAccessTokenCookieName);
    }

    getRedirectOnLogout() {
        var url = Cookies.get(redirectOnLogoutCookieName);
        if (url && url !== "") {
            if (url.startsWith(process.env.REACT_APP_PUBLIC_SITE + "/")) {
                return url;
            }
            else if (url.startsWith(process.env.REACT_APP_ID_SITE + "/")) {
                return url;
            }
            else if (url.startsWith(process.env.REACT_APP_PROVIDER_SITE + "/")) {
                return url;
            }
            else if (url.startsWith(process.env.REACT_APP_NETWORK_SITE + "/")) {
                return url;
            }
            else {
                return null;
            }
        }
        return null;
    }

    removeRedirectOnLogout() {
        Cookies.remove(redirectOnLogoutCookieName, removeCookieOptions);
    }

    redirectUser(providerCount) {
        var redirectUrl = Cookies.get(redirectOnLoginCookieName);
        var isWallistSite = false;
        var isProviderSite = false;
        if (redirectUrl && redirectUrl !== "") {
            if (redirectUrl.startsWith(process.env.REACT_APP_PUBLIC_SITE + "/")) {
                isWallistSite = true;
            }
            else if (redirectUrl.startsWith(process.env.REACT_APP_ID_SITE + "/")) {
                isWallistSite = true;
            }
            else if (redirectUrl.startsWith(process.env.REACT_APP_PROVIDER_SITE + "/")) {
                isProviderSite = true;
                isWallistSite = true;
            }
            else if (redirectUrl.startsWith(process.env.REACT_APP_NETWORK_SITE + "/")) {
                isWallistSite = true;
            }
            else {
                isWallistSite = false;
            }
        }
        if (isWallistSite && redirectUrl) {
            // They are in a process (e.g. profile claiming or clicked a provider link) and need to redirect to complete the flow
            Cookies.remove(redirectOnLoginCookieName, removeCookieOptions);
            if (!isProviderSite) {
                window.location.href = redirectUrl;
            }
            else {
                if (this.getProviderSubdomain()) {
                    // they are auto-logged into the single provider so redirects will work
                    window.location.href = redirectUrl;
                }
                else {
                    if (providerCount > 1) {
                        // TODO: if you have more than 1 provider, it would be nice if we looked up the provider and logged you into it automatically.
                        window.location.href = process.env.REACT_APP_ID_SITE + "/account/select-provider";
                    }
                    else {
                        // they don't have a provider yet and shouldn't be trying to access the provider site
                        window.location.href = process.env.REACT_APP_ID_SITE + "/account/welcome";
                    }
                }
            }
        }
        else {
            if (this.getProviderSubdomain()) {
                // They only have 1 provider and auto-logged in
                window.location.href = this.getProviderUrl();
            }
            else {
                if (providerCount > 1) {
                    // they have more than 1 provider so they need to pick
                    window.location.href = process.env.REACT_APP_ID_SITE + "/account/select-provider";
                }
                else {
                    // they don't have a provider yet
                    window.location.href = process.env.REACT_APP_ID_SITE + "/account/welcome";
                }
            }
        }
    }

    getPublicProfileName() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            var decoded = jwtDecode(token);
            if (decoded.fn && decoded.ln) {
                return decoded.fn + " " + decoded.ln.charAt(0);
            }
            else {
                return "";
            }
        } else {
            return "";
        }
    }

    getFirstName() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            var decoded = jwtDecode(token);
            return decoded.fn;
        } else {
            return "";
        }
    }

    getLastName() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            var decoded = jwtDecode(token);
            return decoded.ln;
        } else {
            return "";
        }
    }

    /**
     * Returns true if the user has not yet set their first name or last name
     * @returns Returns true if the user has not yet set their first name or last name
     */
    accountInfoNeeded() {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            var decoded = jwtDecode(token);
            if (DataUtils.isNotEmpty(decoded.fn) && DataUtils.isNotEmpty(decoded.ln)) {
                return false;
            }
        }
        return true;
    }

    hasAccess(section, feature, action) {
        var token = Cookies.get(userRefreshTokenCookieName);
        if (token) {
            var decoded = jwtDecode(token);
            var roles = null;
            if (section.startsWith('b')) {
                roles = decoded.br;
            }
            else if (section.startsWith('p')) {
                roles = decoded.pr;
            }
            if (roles) {
                let items = roles.split(',').map(item => item.trim());
                if (items.includes(ROLE_ADMIN)) {
                    return true;
                }
                else {
                    if (items.includes(ROLE_VIEWER)) {
                        if (action === VIEW) {
                            return true;
                        }
                        else {
                            return false;
                        }
                    }
                }
                return true;
            }
            else {
                return false;
            }
        }
        return false;
    }

    isImpersonating() {
        if (this.isWallistAdmin() && !this.hasAccess(BILLING, SETTINGS, EDIT)) {
            return true;
        }
        else {
            return false;
        }
    }

    setRedirectOnLogin(url) {
        const expirationTime = 60 * 60 * 1000 * 24 * 3; // 72 hours
        var inThreeDays = new Date(new Date().getTime() + expirationTime);
        const cookieOptions = {
            path: "/",
            secure: true,
            expires: inThreeDays,
            domain: cookieDomain,
        };
        Cookies.set(redirectOnLoginCookieName, url, cookieOptions);
    }

    getCsrfToken() {
        return Cookies.get('wcsrft');
    }
}

export default new TokenService();
